import React, { useRef } from 'react';
import { useAnimate } from 'framer-motion';
import { FiMousePointer } from 'react-icons/fi';
import './notFound.scss';

interface IMouseImageTrail {
  children: React.ReactNode;
  images: string[];
  renderImageBuffer: number;
  rotationRange: number;
}

interface ICalculateDistance {
  (x1: number, y1: number, x2: number, y2: number): number;
}

const NotFound = () => {
  return (
    <MouseImageTrail
      renderImageBuffer={50}
      rotationRange={25}
      images={[
        '/seamansclub-news/assets/images/1.jpg',
        '/seamansclub-news/assets/images/2.jpg',
        '/seamansclub-news/assets/images/3.jpg',
        '/seamansclub-news/assets/images/4.jpg',
        '/seamansclub-news/assets/images/5.jpg',
        '/seamansclub-news/assets/images/6.jpg',
      ]}
    >
      <section className="notFound">
        <p className="box">
          <FiMousePointer />
          <span>404 Not Found</span>
        </p>
      </section>
    </MouseImageTrail>
  );
};

const MouseImageTrail = ({
  children,

  images,

  renderImageBuffer,

  rotationRange,
}: IMouseImageTrail) => {
  const [scope, animate] = useAnimate();

  const lastRenderPosition = useRef({ x: 0, y: 0 });
  const imageRenderCount = useRef(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;

    const distance = calculateDistance(clientX, clientY, lastRenderPosition.current.x, lastRenderPosition.current.y);

    if (distance >= renderImageBuffer) {
      lastRenderPosition.current.x = clientX;
      lastRenderPosition.current.y = clientY;

      renderNextImage();
    }
  };

  const calculateDistance: ICalculateDistance = (x1, y1, x2, y2) => {
    const deltaX = x2 - x1;
    const deltaY = y2 - y1;

    // Using the Pythagorean theorem to calculate the distance
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    return distance;
  };

  const renderNextImage = () => {
    const imageIndex = imageRenderCount.current % images.length;
    const selector = `[data-mouse-move-index="${imageIndex}"]`;

    const el = document.querySelector(selector);

    if (el) {
      const htmlEl = el as HTMLElement;
      htmlEl.style.top = `${lastRenderPosition.current.y}px`;
      htmlEl.style.left = `${lastRenderPosition.current.x}px`;
      htmlEl.style.zIndex = imageRenderCount.current.toString();
    }

    const rotation = Math.random() * rotationRange;

    animate(
      selector,
      {
        opacity: [0, 1],
        transform: [
          `translate(-50%, -25%) scale(0.5) ${imageIndex % 2 ? `rotate(${rotation}deg)` : `rotate(-${rotation}deg)`}`,
          `translate(-50%, -50%) scale(1) ${imageIndex % 2 ? `rotate(-${rotation}deg)` : `rotate(${rotation}deg)`}`,
        ],
      },
      { type: 'spring', damping: 15, stiffness: 200 },
    );

    animate(
      selector,
      {
        opacity: [1, 0],
      },
      { ease: 'linear', duration: 0.5, delay: 5 },
    );

    imageRenderCount.current = imageRenderCount.current + 1;
  };

  return (
    <div ref={scope} className="relative overflow-hidden" onMouseMove={handleMouseMove}>
      {children}

      {images.map((img, index) => (
        <img className="notFound__img" src={img} alt={`Mouse move image ${index}`} key={index} data-mouse-move-index={index} />
      ))}
    </div>
  );
};

export default NotFound;
